export type TableAlign = 'left' | 'center' | 'right' | undefined;

export type MarkdownSegment =
  | { type: 'markdown'; text: string }
  | { type: 'table'; header: string[]; align: TableAlign[]; rows: string[][] };

const DELIMITER_CELL = /^:?-+:?$/;

/** 1 行を「|」で分けてセルにする（先頭・末尾の「|」は取り、「\|」は文字の「|」） */
export const splitTableRow = (line: string): string[] => {
  let body = line.trim();
  if (body.startsWith('|')) body = body.slice(1);
  if (body.endsWith('|') && !body.endsWith('\\|')) body = body.slice(0, -1);
  const cells: string[] = [];
  let current = '';
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (ch === '\\' && body[i + 1] === '|') {
      current += '|';
      i++;
    } else if (ch === '|') {
      cells.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
};

const parseDelimiter = (line: string): TableAlign[] | null => {
  if (!line.includes('-')) return null;
  const cells = splitTableRow(line);
  if (!cells.every((c) => DELIMITER_CELL.test(c))) return null;
  return cells.map((c) => {
    const left = c.startsWith(':');
    const right = c.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    if (left) return 'left';
    return undefined;
  });
};

/** Markdown を、GFM の表と、それ以外の部分に分ける（コードブロックの中は表にしない） */
export const splitMarkdownTables = (source: string): MarkdownSegment[] => {
  const lines = source.split(/\r?\n/);
  const segments: MarkdownSegment[] = [];
  let buffer: string[] = [];
  let inFence = false;

  const flush = () => {
    if (buffer.length > 0) {
      segments.push({ type: 'markdown', text: buffer.join('\n') });
      buffer = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      buffer.push(line);
      i++;
      continue;
    }
    if (!inFence && line.includes('|') && i + 1 < lines.length) {
      const align = parseDelimiter(lines[i + 1]);
      const header = splitTableRow(line);
      if (align && align.length === header.length) {
        const rows: string[][] = [];
        let j = i + 2;
        while (j < lines.length && lines[j].trim() !== '' && lines[j].includes('|')) {
          const cells = splitTableRow(lines[j]);
          rows.push(header.map((_, k) => cells[k] ?? ''));
          j++;
        }
        flush();
        segments.push({ type: 'table', header, align, rows });
        i = j;
        continue;
      }
    }
    buffer.push(line);
    i++;
  }
  flush();
  return segments;
};
